import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Contact, CreateLeadInput, LeadStatus } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useContacts } from '../../api/contacts';
import { usePipelines } from '../../api/pipelines';
import { useLead, useCreateLead, useUpdateLead } from '../../api/leads';
import { AssigneeSelect } from '../../components/assignee/AssigneeSelect';
import { useAuth } from '../../auth/AuthContext';
import { QuickContactModal } from '../contacts/QuickContactModal';

const STATUSES: LeadStatus[] = ['OPEN', 'WON', 'LOST', 'ABANDONED', 'HOLD'];
const CURRENCY_RE = /^[A-Za-z]{3}$/;

type LeadFormFields = {
  isEdit: boolean;
  contactId: string;
  pipelineId: string;
  stageId: string;
  title: string;
  valueAmount: string;
  currency: string;
  expectedCloseAt: string;
};

/** Mirrors the backend's `createLeadBody`/`updateLeadBody` limits (leads.schemas.ts). */
function validate(f: LeadFormFields): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!f.isEdit && !f.contactId) {
    errors.contactId = 'Select a contact.';
  }
  if (!f.isEdit && !f.pipelineId) {
    errors.pipelineId = 'Select a pipeline.';
  }
  if (!f.stageId) {
    errors.stageId = 'Select a stage.';
  }
  const title = f.title.trim();
  if (title.length < 1) {
    errors.title = 'Enter a title.';
  } else if (title.length > 200) {
    errors.title = 'Title must be 200 characters or fewer.';
  }
  if (f.valueAmount.trim() !== '') {
    const value = Number(f.valueAmount);
    if (Number.isNaN(value) || value < 0 || value > 1_000_000_000_000) {
      errors.valueAmount = 'Value must be a non-negative number up to 1,000,000,000,000.';
    }
  }
  if (f.currency.trim() !== '' && !CURRENCY_RE.test(f.currency.trim())) {
    errors.currency = 'Enter a 3-letter ISO-4217 currency code.';
  }
  if (f.expectedCloseAt.trim() !== '' && Number.isNaN(Date.parse(f.expectedCloseAt))) {
    errors.expectedCloseAt = 'Enter a valid date.';
  }
  return errors;
}

export function LeadFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canCreateContact = hasPermission('contacts.create');

  const contacts = useContacts({ pageSize: 500, sortBy: 'displayName', sortOrder: 'asc' });
  const pipelines = usePipelines();
  const existing = useLead(id);
  const create = useCreateLead();
  const update = useUpdateLead(id ?? '');

  const [contactId, setContactId] = useState('');
  const [addingContact, setAddingContact] = useState(false);
  /** A contact made here, shown even before (or beyond) the loaded contact list. */
  const [newContact, setNewContact] = useState<Contact | null>(null);
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [title, setTitle] = useState('');
  const [valueAmount, setValueAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [status, setStatus] = useState<LeadStatus>('OPEN');
  const [expectedCloseAt, setExpectedCloseAt] = useState('');
  /** tenant_user id of the assignee, '' = unassigned. */
  const [assigneeId, setAssigneeId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  function clearFieldError(key: string) {
    setFieldErrors((f) => {
      if (!f[key]) return f;
      const { [key]: _removed, ...rest } = f;
      return rest;
    });
  }

  const stages = useMemo(
    () => pipelines.data?.find((p) => p.id === pipelineId)?.stages ?? [],
    [pipelines.data, pipelineId],
  );

  // Defaults for create
  useEffect(() => {
    if (isEdit || pipelineId || !pipelines.data?.length) return;
    const p = pipelines.data.find((x) => x.isDefault) ?? pipelines.data[0]!;
    setPipelineId(p.id);
    setStageId(p.stages[0]?.id ?? '');
  }, [isEdit, pipelineId, pipelines.data]);

  // Prefill for edit
  useEffect(() => {
    if (!isEdit || !existing.data) return;
    const l = existing.data;
    setContactId(l.contact.id);
    setPipelineId(l.pipeline.id);
    setStageId(l.stage.id);
    setTitle(l.title);
    setValueAmount(l.valueAmount != null ? String(l.valueAmount) : '');
    setCurrency(l.currency);
    setStatus(l.status);
    setExpectedCloseAt(l.expectedCloseAt ? l.expectedCloseAt.slice(0, 10) : '');
    setAssigneeId(l.assignedUser?.tenantUserId ?? '');
  }, [isEdit, existing.data]);

  const busy = create.isPending || update.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate({
      isEdit,
      contactId,
      pipelineId,
      stageId,
      title,
      valueAmount,
      currency,
      expectedCloseAt,
    });
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const value = valueAmount.trim() === '' ? undefined : Number(valueAmount);
    const assignedUserId = assigneeId || (isEdit ? null : undefined);
    const iso = expectedCloseAt
      ? new Date(`${expectedCloseAt}T00:00:00.000Z`).toISOString()
      : undefined;

    try {
      if (isEdit && id) {
        const saved = await update.mutateAsync({
          title: title.trim(),
          stageId,
          status,
          valueAmount: value ?? null,
          currency,
          assignedUserId,
          expectedCloseAt: iso ?? null,
        });
        toast.success('Lead updated.');
        navigate(`/leads/${saved.id}`, { replace: true });
      } else {
        const payload: CreateLeadInput = {
          contactId,
          pipelineId,
          stageId,
          title: title.trim(),
          status,
          currency,
          ...(value !== undefined ? { valueAmount: value } : {}),
          ...(assignedUserId ? { assignedUserId } : {}),
          ...(iso ? { expectedCloseAt: iso } : {}),
        };
        const saved = await create.mutateAsync(payload);
        toast.success('Lead created.');
        navigate(`/leads/${saved.id}`, { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          setFieldErrors(serverFieldErrors);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the lead.';
        setError(message);
        toast.error(message);
      }
    }
  }

  if ((isEdit && existing.isPending) || pipelines.isPending || contacts.isPending) {
    return <PageState kind="loading" message="Loading…" />;
  }
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 560 }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit lead' : 'New lead'}
      </h1>
      <Card>
        <form className="ne-auth__form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Title"
            required
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              clearFieldError('title');
            }}
            error={fieldErrors.title}
          />

          <Select
            label="Contact"
            required
            value={contactId}
            disabled={isEdit}
            options={[
              { value: '', label: 'Select a contact…' },
              ...(newContact && !contacts.data?.items.some((c) => c.id === newContact.id)
                ? [newContact]
                : []
              ).map((c) => ({ value: c.id, label: c.displayName })),
              ...(contacts.data?.items ?? []).map((c) => ({ value: c.id, label: c.displayName })),
            ]}
            onChange={(e) => {
              setContactId(e.target.value);
              clearFieldError('contactId');
            }}
            error={fieldErrors.contactId}
          />
          {!isEdit && canCreateContact ? (
            <div style={{ marginTop: 'calc(-1 * var(--space-2))' }}>
              <Button type="button" variant="secondary" onClick={() => setAddingContact(true)}>
                + New contact
              </Button>
            </div>
          ) : null}

          <Select
            label="Pipeline"
            value={pipelineId}
            disabled={isEdit}
            options={(pipelines.data ?? []).map((p) => ({ value: p.id, label: p.name }))}
            onChange={(e) => {
              setPipelineId(e.target.value);
              const p = pipelines.data?.find((x) => x.id === e.target.value);
              setStageId(p?.stages[0]?.id ?? '');
              clearFieldError('pipelineId');
            }}
            error={fieldErrors.pipelineId}
          />

          <Select
            label="Stage"
            value={stageId}
            options={stages.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(e) => {
              setStageId(e.target.value);
              clearFieldError('stageId');
            }}
            error={fieldErrors.stageId}
          />

          <Select
            label="Status"
            value={status}
            options={STATUSES.map((s) => ({ value: s, label: s }))}
            onChange={(e) => setStatus(e.target.value as LeadStatus)}
          />

          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <div style={{ flex: 1 }}>
              <TextField
                label="Value"
                inputMode="decimal"
                value={valueAmount}
                onChange={(e) => {
                  setValueAmount(e.target.value);
                  clearFieldError('valueAmount');
                }}
                error={fieldErrors.valueAmount}
              />
            </div>
            <div style={{ width: 90 }}>
              <TextField
                label="Currency"
                value={currency}
                maxLength={3}
                onChange={(e) => {
                  setCurrency(e.target.value.toUpperCase());
                  clearFieldError('currency');
                }}
                error={fieldErrors.currency}
              />
            </div>
          </div>

          <TextField
            label="Expected close"
            type="date"
            value={expectedCloseAt}
            onChange={(e) => {
              setExpectedCloseAt(e.target.value);
              clearFieldError('expectedCloseAt');
            }}
            error={fieldErrors.expectedCloseAt}
          />

          <AssigneeSelect
            value={assigneeId}
            currentName={existing.data?.assignedUser?.displayName}
            onChange={(v) => {
              setAssigneeId(v);
              clearFieldError('assignedUserId');
            }}
            error={fieldErrors.assignedUserId}
          />

          {error ? (
            <p className="ne-auth__error" role="alert">
              {error}
            </p>
          ) : null}
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <Button type="submit" disabled={busy || (!isEdit && (!contactId || !stageId))}>
              {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create lead'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(isEdit && id ? `/leads/${id}` : '/leads')}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
      {addingContact ? (
        <QuickContactModal
          onClose={() => setAddingContact(false)}
          onCreated={(c) => {
            setNewContact(c);
            setContactId(c.id);
            clearFieldError('contactId');
            setAddingContact(false);
          }}
        />
      ) : null}
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/leads">← All leads</Link>
      </p>
    </div>
  );
}
